import { ComponentRef, ReactElement, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  ErrorSummary,
  Field,
  Heading,
  Input,
  Label,
  Link,
  Paragraph,
  Skeleton,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { PlusIcon, TrashIcon } from '@navikt/aksel-icons';
import type { ChecklistEditor } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';
import {
  useAcceptSuggestionMutation,
  useChecklistQuery,
  useDiscardChecklistDraftMutation,
  usePublishChecklistMutation,
  useRejectSuggestionMutation,
  useSaveChecklistDraftMutation,
  useSaveChecklistLanguagesMutation,
  useSaveChecklistTranslationsMutation,
  useSetChecklistArchivedMutation,
  useSuggestChecklistTranslationsMutation,
} from '../../../hooks/queries/safetyRounds/useChecklistQueries';
import { LanguageCard } from '../../HRDashboard/HRRiskFactors/components/LanguageCard';
import {
  addCategory,
  addCheckpoint,
  CHECKLIST_FIELD_IDS,
  checkpointNumber,
  type Draft,
  type ChecklistFormError,
  isChanged,
  publishFormErrors,
  removeCategory,
  removeCheckpoint,
  renameCategory,
  toDraft,
  toDraftInput,
  updateCheckpoint,
} from './checklistView';
import { cardStatus } from './checklistView';
import { SuggestionsCard } from './components/SuggestionsCard';
import { ChecklistTranslationPanel } from './components/ChecklistTranslationPanel';
import { ApiErrorUtils } from '../../../utils/api-error.utils';
import { LoadProblem } from '../LoadProblem';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Checklists.module.css';

export const ChecklistEditorPage = (): ReactElement => {
  const { t } = useTranslation();
  const id = Number(useParams().checklistId);
  const { data, isLoading, error } = useChecklistQuery(id);

  const content = () => {
    if (isLoading)
      return (
        <div aria-busy="true" className={layout.loading}>
          <span className="ds-sr-only">{t('safetyRounds.checklist.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={420} />
        </div>
      );
    if (error || !data) return <LoadProblem error={error} subject="checklist" />;
    return <Editor key={id} editor={data} />;
  };

  // Without access, or for a template that does not exist, the page is not shown as an empty editor
  if (ApiErrorUtils.pageProblemOf(error)) return <LoadProblem error={error} subject="checklist" />;

  const status = data ? cardStatus(data, t) : null;
  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          <Link asChild data-size="sm" className={layout.startAligned}>
            <RouterLink to={Routes.HRSafetyRoundChecklistsFullPath}>{t('safetyRounds.checklist.back')}</RouterLink>
          </Link>
          <span className={layout.eyebrow}>{t('safetyRounds.checklist.eyebrow')}</span>
          <Heading level={1} data-size="xl">
            {data?.name ?? t('safetyRounds.checklist.title')}
          </Heading>
          {data && status && (
            <div className={classes.tags}>
              <Tag data-color={status.color} data-size="sm">
                {status.label}
              </Tag>
              <Paragraph data-size="sm" className={layout.subtle}>
                {data.version === null
                  ? t('safetyRounds.checklist.firstVersion')
                  : t('safetyRounds.checklist.versions', { current: data.version, next: data.nextVersion })}
              </Paragraph>
            </div>
          )}
        </div>
      </section>
      {content()}
    </div>
  );
};

const Editor = ({ editor }: { editor: ChecklistEditor }): ReactElement => {
  const { t } = useTranslation();
  const id = editor.id;
  const saved = toDraft(editor);
  const [draft, setDraft] = useState<Draft>(saved);
  const [translating, setTranslating] = useState<string | null>(null);
  const [changeNote, setChangeNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [errors, setErrors] = useState<ChecklistFormError[] | null>(null);
  const [published, setPublished] = useState<number | null>(null);
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);

  const saveDraft = useSaveChecklistDraftMutation(id);
  const discard = useDiscardChecklistDraftMutation(id);
  const publish = usePublishChecklistMutation(id);
  const saveLanguages = useSaveChecklistLanguagesMutation(id);
  const saveTranslations = useSaveChecklistTranslationsMutation(id);
  const suggestTranslations = useSuggestChecklistTranslationsMutation(id);
  const accept = useAcceptSuggestionMutation(id);
  const reject = useRejectSuggestionMutation(id);
  const archive = useSetChecklistArchivedMutation(id);

  const changed = isChanged(draft, saved);
  const archived = editor.status === 'archived';

  useEffect(() => {
    if (errors?.length) summaryRef.current?.focus();
  }, [errors]);

  // What the server answers becomes the form, so new keys and numbers follow
  const reset = (next: ChecklistEditor) => setDraft(toDraft(next));

  /** Saves what HR has typed first, so a change elsewhere does not throw it away */
  const afterSaving = (action: () => void) => {
    if (!changed) return action();
    saveDraft.mutate(toDraftInput(draft), { onSuccess: (next) => { reset(next); action(); } });
  };

  const submit = () => {
    const found = publishFormErrors(draft, changeNote, t);
    setErrors(found);
    if (found.length > 0) return;
    const doPublish = () =>
      publish.mutate(
        { changeNote: changeNote.trim(), notifySafetyReps: notify },
        {
          onSuccess: (next) => {
            reset(next);
            setChangeNote('');
            setErrors(null);
            setPublished(next.version);
          },
        },
      );
    if (changed) saveDraft.mutate(toDraftInput(draft), { onSuccess: (next) => { reset(next); doPublish(); } });
    else doPublish();
  };

  const errorFor = (fieldId: string) => errors?.find((error) => error.fieldId === fieldId)?.message;

  if (translating) {
    const language = editor.languages.find((candidate) => candidate.code === translating);
    if (language)
      return (
        <div className={classes.content}>
          <ChecklistTranslationPanel
            language={language}
            onBack={() => setTranslating(null)}
            onSave={(entries) => saveTranslations.mutate({ language: language.code, entries })}
            saving={saveTranslations.isPending}
            saveFailed={saveTranslations.isError}
            onSuggest={() => suggestTranslations.mutate(language.code)}
            suggesting={suggestTranslations.isPending}
            suggestFailed={suggestTranslations.isError}
          />
        </div>
      );
  }

  return (
    <div className={layout.layout}>
      <div className={layout.main}>
        {published !== null && (
          <Alert data-color="success" data-size="sm" role="status">
            <Paragraph data-size="sm">{t('safetyRounds.checklist.published', { version: published })}</Paragraph>
          </Alert>
        )}
        {archived && (
          <Alert data-color="warning" data-size="sm">
            <Paragraph data-size="sm">{t('safetyRounds.checklist.archivedInfo')}</Paragraph>
          </Alert>
        )}
        <section aria-labelledby="checklist-details" className={layout.card} data-gap="md">
          <Heading level={2} data-size="sm" id="checklist-details">
            {t('safetyRounds.checklist.details')}
          </Heading>
          <Field>
            <Label htmlFor={CHECKLIST_FIELD_IDS.name}>{t('safetyRounds.checklist.name')}</Label>
            <Input
              id={CHECKLIST_FIELD_IDS.name}
              value={draft.name}
              aria-invalid={errorFor(CHECKLIST_FIELD_IDS.name) ? true : undefined}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
            {errorFor(CHECKLIST_FIELD_IDS.name) && (
              <ValidationMessage>{errorFor(CHECKLIST_FIELD_IDS.name)}</ValidationMessage>
            )}
          </Field>
          <Field>
            <Label htmlFor="checklist-description">{t('safetyRounds.checklist.description')}</Label>
            <Textarea
              id="checklist-description"
              rows={2}
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            />
          </Field>
        </section>

        {draft.categories.map((category, categoryIndex) => {
          const nameError = errorFor(CHECKLIST_FIELD_IDS.category(category.id));
          const emptyError = errorFor(CHECKLIST_FIELD_IDS.addCheckpoint(category.id));
          return (
            <section
              key={category.id}
              aria-label={t('safetyRounds.checklist.categoryLabel', { number: categoryIndex + 1 })}
              className={layout.card}
              data-gap="md"
            >
              <div className={classes.categoryHead}>
                <span className={classes.categoryNumber}>{categoryIndex + 1}.</span>
                <Field>
                  <Label htmlFor={CHECKLIST_FIELD_IDS.category(category.id)}>{t('safetyRounds.checklist.category')}</Label>
                  <Input
                    id={CHECKLIST_FIELD_IDS.category(category.id)}
                    value={category.name}
                    aria-invalid={nameError ? true : undefined}
                    onChange={(event) => setDraft(renameCategory(draft, category.id, event.target.value))}
                  />
                  {nameError && <ValidationMessage>{nameError}</ValidationMessage>}
                </Field>
                <Button
                  data-size="sm"
                  variant="tertiary"
                  aria-label={t('safetyRounds.checklist.removeCategory', { name: category.name || categoryIndex + 1 })}
                  onClick={() => setDraft(removeCategory(draft, category.id))}
                >
                  {t('safetyRounds.checklist.remove')}
                </Button>
              </div>
              <ol className={classes.checkpoints}>
                {category.checkpoints.map((point, index) => {
                  const number = checkpointNumber(categoryIndex, index);
                  return (
                    <li key={point.id} className={classes.checkpoint}>
                      <span className={classes.checkpointNumber} aria-hidden="true">
                        {number}
                      </span>
                      <Textarea
                        data-size="sm"
                        rows={1}
                        className={classes.checkpointText}
                        value={point.text}
                        placeholder={t('safetyRounds.checklist.checkpointPlaceholder')}
                        aria-label={t('safetyRounds.checklist.checkpointLabel', { number })}
                        onChange={(event) =>
                          setDraft(updateCheckpoint(draft, category.id, point.id, { text: event.target.value }))
                        }
                      />
                      <Checkbox
                        data-size="sm"
                        label={
                          <>
                            {t('safetyRounds.checklist.required')}
                            <span className="ds-sr-only"> ({t('safetyRounds.checklist.checkpointLabel', { number })})</span>
                          </>
                        }
                        checked={point.required}
                        onChange={(event) =>
                          setDraft(updateCheckpoint(draft, category.id, point.id, { required: event.target.checked }))
                        }
                      />
                      <Button
                        data-size="sm"
                        variant="tertiary"
                        icon
                        aria-label={t('safetyRounds.checklist.removeCheckpoint', { number })}
                        onClick={() => setDraft(removeCheckpoint(draft, category.id, point.id))}
                      >
                        <TrashIcon aria-hidden="true" />
                      </Button>
                    </li>
                  );
                })}
              </ol>
              <div>
                <Button
                  id={CHECKLIST_FIELD_IDS.addCheckpoint(category.id)}
                  data-size="sm"
                  variant="secondary"
                  onClick={() => setDraft(addCheckpoint(draft, category.id))}
                >
                  <PlusIcon aria-hidden="true" />
                  {t('safetyRounds.checklist.addCheckpoint')}
                </Button>
                {emptyError && <ValidationMessage>{emptyError}</ValidationMessage>}
              </div>
            </section>
          );
        })}
        <div>
          <Button id={CHECKLIST_FIELD_IDS.addCategory} data-size="sm" variant="secondary" onClick={() => setDraft(addCategory(draft))}>
            <PlusIcon aria-hidden="true" />
            {t('safetyRounds.checklist.addCategory')}
          </Button>
          {errorFor(CHECKLIST_FIELD_IDS.addCategory) && (
            <ValidationMessage>{errorFor(CHECKLIST_FIELD_IDS.addCategory)}</ValidationMessage>
          )}
        </div>

        {editor.suggestions.length > 0 && (
          <SuggestionsCard
            suggestions={editor.suggestions}
            onAccept={(suggestionId) => afterSaving(() => accept.mutate(suggestionId, { onSuccess: reset }))}
            onReject={(suggestionId, comment) => reject.mutate({ suggestionId, comment })}
            failed={accept.isError || reject.isError}
          />
        )}

        <section aria-labelledby="checklist-publish" className={layout.card} data-gap="md" data-highlight="true">
          <Heading level={2} data-size="sm" id="checklist-publish">
            {t('safetyRounds.checklist.publishTitle')}
          </Heading>
          <Field>
            <Label htmlFor={CHECKLIST_FIELD_IDS.changeNote}>{t('safetyRounds.checklist.changeNote')}</Label>
            <Paragraph data-size="sm" className={layout.subtle} id="checklist-change-note-description">
              {t('safetyRounds.checklist.changeNoteDescription')}
            </Paragraph>
            <Textarea
              id={CHECKLIST_FIELD_IDS.changeNote}
              rows={2}
              value={changeNote}
              aria-describedby="checklist-change-note-description"
              aria-invalid={errorFor(CHECKLIST_FIELD_IDS.changeNote) ? true : undefined}
              onChange={(event) => setChangeNote(event.target.value)}
            />
            {errorFor(CHECKLIST_FIELD_IDS.changeNote) && (
              <ValidationMessage>{errorFor(CHECKLIST_FIELD_IDS.changeNote)}</ValidationMessage>
            )}
          </Field>
          <Checkbox
            data-size="sm"
            label={t('safetyRounds.checklist.notify')}
            description={t('safetyRounds.checklist.notifyDescription', { count: editor.activePlans })}
            checked={notify}
            onChange={(event) => setNotify(event.target.checked)}
          />
          {errors && errors.length > 0 && (
            <ErrorSummary ref={summaryRef}>
              <ErrorSummary.Heading>{t('safetyRounds.checklist.errorSummary')}</ErrorSummary.Heading>
              <ErrorSummary.List>
                {errors.map((error) => (
                  <ErrorSummary.Item key={error.fieldId}>
                    <ErrorSummary.Link href={`#${error.fieldId}`}>{error.message}</ErrorSummary.Link>
                  </ErrorSummary.Item>
                ))}
              </ErrorSummary.List>
            </ErrorSummary>
          )}
          {(publish.isError || saveDraft.isError) && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
            </Alert>
          )}
          <div className={layout.actions}>
            <Button data-size="sm" onClick={submit} aria-busy={publish.isPending || undefined}>
              {t('safetyRounds.checklist.publish', { version: editor.nextVersion })}
            </Button>
            <Button
              data-size="sm"
              variant="secondary"
              aria-busy={saveDraft.isPending || undefined}
              onClick={() => (changed ? saveDraft.mutate(toDraftInput(draft), { onSuccess: reset }) : undefined)}
            >
              {t('safetyRounds.checklist.saveDraft')}
            </Button>
            {changed && (
              <Button data-size="sm" variant="tertiary" onClick={() => setDraft(saved)}>
                {t('safetyRounds.checklist.undo')}
              </Button>
            )}
          </div>
          {!changed && saveDraft.isSuccess && (
            <Paragraph data-size="sm" role="status" className={layout.subtle}>
              {t('safetyRounds.checklist.draftSaved')}
            </Paragraph>
          )}
        </section>
      </div>

      <aside className={layout.side}>
        <LanguageCard
          languages={editor.languages}
          selectable={editor.selectableLanguages}
          locked={false}
          saving={saveLanguages.isPending}
          saveFailed={saveLanguages.isError}
          intro={t('safetyRounds.checklist.languagesIntro')}
          onSave={(languages, onSaved) =>
            afterSaving(() =>
              saveLanguages.mutate(languages, {
                onSuccess: (next) => {
                  reset(next);
                  onSaved?.();
                },
              }),
            )
          }
          onTranslate={(code) => afterSaving(() => setTranslating(code))}
        />
        {editor.hasDraft && editor.version !== null && (
          <section aria-labelledby="checklist-discard" className={layout.sideCard} data-gap="sm">
            <Heading level={2} data-size="2xs" id="checklist-discard">
              {t('safetyRounds.checklist.discardTitle')}
            </Heading>
            <Paragraph data-size="sm">{t('safetyRounds.checklist.discardText', { version: editor.version })}</Paragraph>
            <Button data-size="sm" variant="secondary" onClick={() => discard.mutate(undefined, { onSuccess: reset })}>
              {t('safetyRounds.checklist.discard')}
            </Button>
          </section>
        )}
        {editor.version !== null && (
          <section aria-labelledby="checklist-archive" className={layout.sideCard} data-gap="sm">
            <Heading level={2} data-size="2xs" id="checklist-archive">
              {archived ? t('safetyRounds.checklist.unarchiveTitle') : t('safetyRounds.checklist.archiveTitle')}
            </Heading>
            <Paragraph data-size="sm">
              {archived ? t('safetyRounds.checklist.unarchiveText') : t('safetyRounds.checklist.archiveText')}
            </Paragraph>
            <Button data-size="sm" variant="secondary" onClick={() => archive.mutate(!archived)}>
              {archived ? t('safetyRounds.checklist.unarchive') : t('safetyRounds.checklist.archive')}
            </Button>
          </section>
        )}
      </aside>
    </div>
  );
};
